import { useId, useState } from 'react';
import { FilterToolbarButton } from '@components/ui/FilterMenu';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';

import {
  CLAIM_ROW_GROUP_BY_KEYS,
  CLAIM_ROW_GROUP_MENU_LABELS,
  CLAIM_ROW_GROUP_TRIGGER_LABELS,
  type ClaimRowGroupBy,
} from './claimRowGrouping';

export interface ClaimGroupMenuButtonProps {
  value: ClaimRowGroupBy;
  onChange: (value: ClaimRowGroupBy) => void;
}

/**
 * "Group:" on the Claims to fix toolbar (GDN-3383).
 *
 * The `SortMenuButton` pattern, in this feature rather than in the shared
 * FilterMenu kit: that button hardcodes its own prefix and icon, and only
 * one queue offers grouping today. If a second surface asks for it, the two
 * collapse into one generalized trigger upstream.
 */
export const ClaimGroupMenuButton = ({ value, onChange }: ClaimGroupMenuButtonProps) => {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const menuId = useId();
  const open = Boolean(anchor);

  return (
    <>
      <FilterToolbarButton
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={event => setAnchor(event.currentTarget)}
        startIcon={<ViewAgendaOutlinedIcon />}
      >
        Group: {CLAIM_ROW_GROUP_TRIGGER_LABELS[value]}
      </FilterToolbarButton>
      <Menu
        anchorEl={anchor}
        id={menuId}
        open={open}
        slotProps={{
          list: { 'aria-label': 'Group claims' },
          paper: {
            sx: {
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.14)',
              /* A flat CSS floor, not a measured `anchor.offsetWidth`: the
                 widest trigger this button ever renders ("Group: Reason") is
                 comfortably under 220px, so measuring the DOM to reach the
                 same number is JavaScript doing a stylesheet's job.
                 `SortMenuButton` still measures; left alone, since it is the
                 shared kit and not this ticket. */
              minWidth: 220,
              mt: 0.5,
            },
          },
        }}
        onClose={() => setAnchor(null)}
      >
        {CLAIM_ROW_GROUP_BY_KEYS.map(option => {
          const isSelected = option === value;
          return (
            <MenuItem
              key={option}
              selected={isSelected}
              onClick={() => {
                onChange(option);
                setAnchor(null);
              }}
            >
              <ListItemText>{CLAIM_ROW_GROUP_MENU_LABELS[option]}</ListItemText>
              <ListItemIcon sx={{ justifyContent: 'flex-end', minWidth: 28 }}>
                {isSelected && <CheckRoundedIcon color="primary" fontSize="small" />}
              </ListItemIcon>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
